import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SAVED_REFRESH_DEBOUNCE_MS } from './useLeadRowSync';

/**
 * What a grid note save refreshes, the way an inline field save does
 * (`useLeadRowSync`): the lead's detail query at once, so an open or
 * later-opened drawer shows the note, and the event's KPI tiles once per
 * burst, 2 s after the last save, so the Notes tile follows. The table's
 * list is NOT reloaded: the saved note already updated its row.
 *
 * No CRM re-check: a note changes nothing the CRM match reads. Kept apart
 * from `useLeadRowSync`'s own debounce so the reviewed inline-edit store is
 * untouched; a note burst and a field burst may each refresh the tiles once.
 *
 * Used by `useLeadNoteSync`, so it lives with that store's host —
 * `BatchScreen`, or a table hosting its own. Flushes on the host's unmount,
 * so a pending refresh is never left to a timer nobody owns.
 */
export function useNoteSaveRefresh(): (leadId: string, conferenceId: string | null) => void {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const [debounced] = useState(() => {
    const conferenceIds = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    function flush() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      const ids = Array.from(conferenceIds);
      conferenceIds.clear();
      for (const id of ids) {
        void queryClientRef.current.invalidateQueries({ queryKey: ['batchAggregations', id] });
      }
    }
    return {
      saved(conferenceId: string | null) {
        if (!conferenceId) return;
        conferenceIds.add(conferenceId);
        if (timer !== null) clearTimeout(timer);
        timer = setTimeout(flush, SAVED_REFRESH_DEBOUNCE_MS);
      },
      flushIfPending() {
        if (timer !== null) flush();
      },
    };
  });

  useEffect(() => () => debounced.flushIfPending(), [debounced]);

  return useCallback(
    (leadId: string, conferenceId: string | null) => {
      void queryClientRef.current.invalidateQueries({ queryKey: ['leadDetail', leadId] });
      debounced.saved(conferenceId);
    },
    [debounced],
  );
}
